import React, { useState, useEffect, useCallback } from "react";
import { Plus, Edit3, Trash2, ChevronUp, ChevronDown, Copy, Eye, Upload, Archive, X, Layers } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import { Markdown } from "../components/Markdown";
import { SECTION_META, youtubeId } from "./CourseDetail";
import { ImportCourseDrawer } from "./AdminLearningImport";
import { toast } from "sonner";

const STAGE_NAMES = ["ENTER", "REMEMBER", "EXPLORE", "DISCERN", "INTEGRATE", "EMBODY", "SYNTHESIZE", "RETURN"];
const inputCls = "w-full rounded-lg border px-3 py-2 text-sm bg-transparent";
const card = { background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" };
const pill = { background: "var(--burgundy)", color: "var(--bg-cream)" };

const Field = ({ label, hint, children }) => (
  <label className="block mb-3">
    <span className="block text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: "var(--gold-dark)" }}>{label}</span>
    {children}
    {hint && <span className="block text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{hint}</span>}
  </label>
);

const StatusBadge = ({ status }) => {
  const map = { published: { bg: "var(--gold)", fg: "var(--burgundy)" }, draft: { bg: "var(--bg-cream)", fg: "var(--text-muted)" }, archived: { bg: "var(--text-obsidian)", fg: "var(--bg-cream)" } };
  const c = map[status] || map.draft;
  return <span className="rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest" style={{ background: c.bg, color: c.fg }}>{status}</span>;
};

const Drawer = ({ open, onClose, title, children, wide }) => open ? (
  <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(48,43,41,0.55)" }} onClick={onClose}>
    <div className={`w-full ${wide ? "max-w-4xl" : "max-w-2xl"} h-full overflow-y-auto p-6 sm:p-8`} onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg-cream)" }}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>{title}</h2>
        <button onClick={onClose} data-testid="drawer-close-btn" className="p-2 rounded-full hover:bg-[color:var(--burgundy-tint)]"><X className="w-4 h-4" /></button>
      </div>
      {children}
    </div>
  </div>
) : null;

const StatusSelect = ({ value, onChange, testid }) => (
  <select data-testid={testid} value={value || "draft"} onChange={(e) => onChange(e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
    <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
  </select>
);

// -------------------- Courses --------------------
export function LearningPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/content/courses");
    setRows((data || []).sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.stage ?? 0) - (b.stage ?? 0)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (doc) => {
    try {
      if (doc.__new) {
        const { __new, ...b } = doc;
        const { data } = await api.post("/admin/content/courses", { ...b, order: rows.length });
        toast("Course created"); setEditing(data);
      } else {
        const { id, __new, resources, ...b } = doc;
        await api.patch(`/admin/content/courses/${id}`, b); toast("Course saved"); setEditing(null);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= rows.length) return;
    const order = [...rows]; [order[i], order[j]] = [order[j], order[i]];
    await api.post("/admin/learning/courses/reorder", { order: order.map((c) => c.id) }); load();
  };

  const duplicate = async (id) => { await api.post(`/admin/learning/courses/${id}/duplicate`); toast("Course duplicated as draft"); load(); };
  const setStatus = async (c, status) => { await api.patch(`/admin/content/courses/${c.id}`, { status }); toast(`Course ${status}`); load(); };
  const remove = async (c) => {
    if (c.status === "published") { toast.error("Unpublish or archive a course before deleting it."); return; }
    if (!window.confirm("Delete this course and all its Encounters? Member progress records will remain but will no longer reference a course. Prefer Archive if members have begun it.")) return;
    await api.delete(`/admin/content/courses/${c.id}`); toast("Course removed"); load();
  };

  return (
    <div data-testid="admin-learning-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="eyebrow mb-1">Admin · Learning Circle</div>
          <h1 className="font-serif text-3xl" style={{ color: "var(--burgundy)" }}>Courses &amp; Encounters.</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Create, arrange, preview and publish courses without code. Each Encounter follows Enter → Teaching → Ask → Wait → Engage → Reflect → Embody → Advance.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="course-import-btn" onClick={() => setImporting(true)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>
            <Upload className="w-4 h-4" /> Import Learning Circle Course
          </button>
          <button data-testid="course-new-btn" onClick={() => setEditing({ __new: true, status: "draft", stage: 1, teacher: "Yisra'el Apriale", title: "" })}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium" style={pill}>
            <Plus className="w-4 h-4" /> New course
          </button>
        </div>
      </div>

      <div className="grid gap-3">
        {rows.length === 0 && <div className="rounded-2xl border p-8 text-center" style={{ ...card, color: "var(--text-muted)" }}>No courses yet.</div>}
        {rows.map((c, i) => (
          <div key={c.id} data-testid={`course-row-${c.id}`} className="rounded-2xl border p-4 flex flex-wrap items-center gap-3" style={card}>
            <div className="w-14 h-14 rounded-lg flex flex-col items-center justify-center font-mono text-[10px] leading-tight flex-shrink-0 overflow-hidden" style={{ background: "var(--burgundy)", color: "var(--gold)" }}>
              {c.art_url ? <img src={assetUrl(c.art_url)} alt="" className="w-full h-full object-cover" /> : <>{String(c.stage).padStart(2, "0")}<span>{STAGE_NAMES[c.stage - 1]?.slice(0, 5)}</span></>}
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-serif text-lg leading-tight" style={{ color: "var(--burgundy)" }}>{c.title}</div>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>Stage {String(c.stage).padStart(2, "0")} {STAGE_NAMES[c.stage - 1]} · {c.teacher || "—"} · {c.lesson_count} published Encounter{c.lesson_count === 1 ? "" : "s"}</div>
            </div>
            <StatusBadge status={c.status || "draft"} />
            <div className="flex items-center gap-1 flex-wrap">
              <button data-testid={`course-up-${c.id}-btn`} onClick={() => move(i, -1)} className="p-1.5" title="Move up"><ChevronUp className="w-4 h-4" /></button>
              <button data-testid={`course-down-${c.id}-btn`} onClick={() => move(i, 1)} className="p-1.5" title="Move down"><ChevronDown className="w-4 h-4" /></button>
              <button data-testid={`course-encounters-${c.id}-btn`} onClick={() => setManaging(c)} className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}><Layers className="w-3.5 h-3.5" /> Encounters</button>
              <a data-testid={`course-preview-${c.id}-link`} href={`/learn/${c.id}?preview=1`} target="_blank" rel="noreferrer" className="p-1.5" title="Preview"><Eye className="w-4 h-4" /></a>
              <button data-testid={`course-edit-${c.id}-btn`} onClick={() => setEditing(c)} className="p-1.5" title="Edit"><Edit3 className="w-4 h-4" /></button>
              <button data-testid={`course-duplicate-${c.id}-btn`} onClick={() => duplicate(c.id)} className="p-1.5" title="Duplicate"><Copy className="w-4 h-4" /></button>
              {c.status === "published"
                ? <button data-testid={`course-unpublish-${c.id}-btn`} onClick={() => setStatus(c, "draft")} className="text-xs ink-link px-1" style={{ color: "var(--burgundy)" }}>Unpublish</button>
                : <button data-testid={`course-publish-${c.id}-btn`} onClick={() => setStatus(c, "published")} className="text-xs ink-link px-1" style={{ color: "var(--burgundy)" }}>Publish</button>}
              {c.status !== "archived" && <button data-testid={`course-archive-${c.id}-btn`} onClick={() => setStatus(c, "archived")} className="p-1.5" title="Archive"><Archive className="w-4 h-4" /></button>}
              <button data-testid={`course-delete-${c.id}-btn`} onClick={() => remove(c)} className="p-1.5" title="Delete"><Trash2 className="w-4 h-4" style={{ color: "var(--rose-dark)" }} /></button>
            </div>
          </div>
        ))}
      </div>

      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing?.__new ? "New course" : "Edit course"}>
        {editing && <CourseForm doc={editing} onChange={setEditing} onSave={save} />}
      </Drawer>
      <Drawer wide open={!!managing} onClose={() => { setManaging(null); load(); }} title={`Encounters — ${managing?.title || ""}`}>
        {managing && <EncountersEditor course={managing} />}
      </Drawer>
      <Drawer wide open={importing} onClose={() => { setImporting(false); load(); }} title="Import Learning Circle Course">
        {importing && <ImportCourseDrawer onDone={load} />}
      </Drawer>
    </div>
  );
}

function CourseForm({ doc, onChange, onSave }) {
  const set = (k, v) => onChange({ ...doc, [k]: v });
  const uploadCover = async (file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/learning/courses/${doc.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("art_url", data.art_url); toast("Cover uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  return (
    <div>
      <Field label="Title"><input data-testid="course-title-input" value={doc.title || ""} onChange={(e) => set("title", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      <Field label="Subtitle / Tagline"><input data-testid="course-tagline-input" value={doc.tagline || doc.subtitle || ""} onChange={(e) => onChange({ ...doc, subtitle: e.target.value, tagline: e.target.value })} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      <Field label="Teacher"><input data-testid="course-teacher-input" value={doc.teacher || ""} onChange={(e) => set("teacher", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      <Field label="Journey stage"><select data-testid="course-stage-select" value={doc.stage || 1} onChange={(e) => set("stage", Number(e.target.value))} className={inputCls} style={{ borderColor: "var(--border-cream)" }}>
        {STAGE_NAMES.map((n, i) => <option key={n} value={i + 1}>{String(i + 1).padStart(2, "0")} · {n}</option>)}
      </select></Field>
      <Field label="Estimated duration"><input value={doc.duration || ""} onChange={(e) => set("duration", e.target.value)} placeholder="e.g. 7 Encounters, unhurried" className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      <Field label="Description" hint="Markdown supported: blank line between paragraphs, **bold**, *italic*, > quotation, - lists."><textarea data-testid="course-description-input" rows={6} value={doc.description || ""} onChange={(e) => set("description", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      <Field label="Cover image">
        <div className="flex items-center gap-3">
          {doc.art_url && <img src={assetUrl(doc.art_url)} alt="" className="w-16 h-16 rounded-lg object-cover" />}
          <input value={doc.art_url || ""} onChange={(e) => set("art_url", e.target.value)} placeholder="Paste an image URL…" className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
          {doc.id && !doc.__new && (
            <label className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-2 border cursor-pointer whitespace-nowrap" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>
              <Upload className="w-3.5 h-3.5" /> Upload
              <input data-testid="course-cover-upload-input" type="file" accept=".jpg,.jpeg,.png,.webp,.gif" className="hidden" onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])} />
            </label>
          )}
        </div>
        {doc.__new && <span className="block text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Save the course first to upload a cover file.</span>}
      </Field>
      <label className="inline-flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={!!doc.featured} onChange={(e) => set("featured", e.target.checked)} />Featured on Home</label>
      <Field label="Status"><StatusSelect testid="course-status-select" value={doc.status} onChange={(v) => set("status", v)} /></Field>
      {doc.id && !doc.__new && <ResourcesEditor ownerType="course" ownerId={doc.id} />}
      <div className="flex items-center gap-3 mt-4">
        <button data-testid="course-save-btn" onClick={() => onSave(doc)} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium" style={pill}>Save</button>
        {doc.id && !doc.__new && <a href={`/learn/${doc.id}?preview=1`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs ink-link" style={{ color: "var(--burgundy)" }}><Eye className="w-3.5 h-3.5" /> Preview</a>}
      </div>
    </div>
  );
}

// -------------------- Encounters --------------------
function EncountersEditor({ course }) {
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/courses/${course.id}/lessons`);
    setRows(data || []);
  }, [course.id]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim()) return;
    const { data } = await api.post(`/admin/courses/${course.id}/lessons`, { title: title.trim(), status: "draft" });
    setTitle(""); toast("Encounter added as draft"); await load(); setEditing(data);
  };
  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= rows.length) return;
    const order = [...rows]; [order[i], order[j]] = [order[j], order[i]];
    await api.post(`/admin/courses/${course.id}/lessons/reorder`, { order: order.map((l) => l.id) }); load();
  };
  const duplicate = async (id) => { await api.post(`/admin/learning/encounters/${id}/duplicate`); toast("Encounter duplicated"); load(); };
  const setStatus = async (l, status) => { await api.patch(`/admin/lessons/${l.id}`, { status }); toast(`Encounter ${status}`); load(); };
  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.title}"? Only unpublished Encounters without member progress can be deleted.`)) return;
    try { await api.delete(`/admin/lessons/${l.id}`); toast("Encounter removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete — archive instead"); }
  };

  return (
    <div>
      <div className="mb-6 rounded-lg border p-3 flex gap-2" style={card}>
        <input data-testid="encounter-new-title-input" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New Encounter title" className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
        <button data-testid="encounter-add-btn" onClick={add} className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium whitespace-nowrap" style={pill}><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
      {rows.length === 0 && <div className="text-sm text-center py-6" style={{ color: "var(--text-muted)" }}>No Encounters yet.</div>}
      <div className="space-y-2">
        {rows.map((l, i) => (
          <div key={l.id} data-testid={`encounter-row-${l.id}`} className="rounded-lg border p-3 flex flex-wrap items-center gap-2" style={{ borderColor: "var(--border-cream)" }}>
            <div className="font-mono text-xs w-6" style={{ color: "var(--gold-dark)" }}>{String(i + 1).padStart(2, "0")}</div>
            <div className="flex-1 min-w-[160px] font-serif" style={{ color: "var(--burgundy)" }}>{l.title}</div>
            <StatusBadge status={l.status || "published"} />
            <button data-testid={`encounter-up-${l.id}-btn`} onClick={() => move(i, -1)} className="p-1"><ChevronUp className="w-4 h-4" /></button>
            <button data-testid={`encounter-down-${l.id}-btn`} onClick={() => move(i, 1)} className="p-1"><ChevronDown className="w-4 h-4" /></button>
            <button data-testid={`encounter-edit-${l.id}-btn`} onClick={() => setEditing(l)} className="p-1" title="Edit"><Edit3 className="w-4 h-4" /></button>
            <button data-testid={`encounter-duplicate-${l.id}-btn`} onClick={() => duplicate(l.id)} className="p-1" title="Duplicate"><Copy className="w-4 h-4" /></button>
            {l.status === "published"
              ? <button data-testid={`encounter-unpublish-${l.id}-btn`} onClick={() => setStatus(l, "draft")} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>Unpublish</button>
              : <button data-testid={`encounter-publish-${l.id}-btn`} onClick={() => setStatus(l, "published")} className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>Publish</button>}
            {l.status !== "archived" && <button data-testid={`encounter-archive-${l.id}-btn`} onClick={() => setStatus(l, "archived")} className="p-1" title="Archive"><Archive className="w-4 h-4" /></button>}
            <button data-testid={`encounter-delete-${l.id}-btn`} onClick={() => remove(l)} className="p-1" title="Delete"><Trash2 className="w-4 h-4" style={{ color: "var(--rose-dark)" }} /></button>
          </div>
        ))}
      </div>

      <Drawer wide open={!!editing} onClose={() => setEditing(null)} title={`Edit Encounter · ${editing?.title || ""}`}>
        {editing && <EncounterForm course={course} doc={editing} onSaved={() => { setEditing(null); load(); }} />}
      </Drawer>
    </div>
  );
}

function EncounterForm({ course, doc, onSaved }) {
  const [d, setD] = useState({ ...doc, sections: { ...(doc.sections || {}) } });
  const [previewKeys, setPreviewKeys] = useState({});
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setSec = (k, v) => setD((x) => ({ ...x, sections: { ...x.sections, [k]: v } }));

  const save = async (status) => {
    try {
      const { id, course_id, index, created_at, updated_at, resources, ...body } = d;
      await api.patch(`/admin/lessons/${id}`, status ? { ...body, status } : body);
      toast(status === "published" ? "Encounter published" : "Encounter saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2"><Field label="Title"><input data-testid="encounter-title-input" value={d.title || ""} onChange={(e) => set("title", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field></div>
        <Field label="Status"><StatusSelect testid="encounter-status-select" value={d.status} onChange={(v) => set("status", v)} /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Lesson video (YouTube, unlisted)" hint="Paste a watch, youtu.be or Shorts link. Shown only to members with course access. Unlisted links can still be shared by anyone who has them.">
          <div className="flex gap-2">
            <input data-testid="encounter-video-url-input" value={d.video_url || ""} onChange={(e) => set("video_url", e.target.value)} placeholder="https://www.youtube.com/watch?v=…" className={inputCls} style={{ borderColor: d.video_url && !youtubeId(d.video_url) ? "var(--rose-dark)" : "var(--border-cream)" }} />
            {d.video_url && <button type="button" data-testid="encounter-video-clear-btn" onClick={() => set("video_url", "")} className="rounded-full px-3 text-xs border whitespace-nowrap" style={{ borderColor: "var(--rose-dark)", color: "var(--rose-dark)" }}>Remove</button>}
          </div>
          {d.video_url && !youtubeId(d.video_url) && <div className="text-[11px] mt-1" style={{ color: "var(--rose-dark)" }} data-testid="encounter-video-invalid">Not a recognized YouTube link.</div>}
          {youtubeId(d.video_url) && <div className="text-[11px] mt-1" style={{ color: "var(--teal-deep)" }} data-testid="encounter-video-valid">Video ID {youtubeId(d.video_url)} will embed on the Encounter.</div>}
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Audio URL (optional)"><input value={d.audio_url || ""} onChange={(e) => set("audio_url", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
        <Field label="Suggested time (min, optional)"><input type="number" value={d.duration_min || ""} onChange={(e) => set("duration_min", e.target.value ? Number(e.target.value) : null)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} /></Field>
      </div>

      <div className="rounded-xl border p-3 mb-5 text-[11px]" style={{ ...card, color: "var(--text-muted)" }}>
        Formatting: leave a blank line between paragraphs · **bold** · *italic* · # Heading · &gt; quotation · - bullet · 1. numbered · [link text](https://…). Any section may be left empty.
      </div>

      {SECTION_META.map((m) => (
        <div key={m.key} className="mb-5" data-testid={`encounter-section-${m.key}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>{m.label} <span className="normal-case tracking-normal font-sans italic" style={{ color: "var(--text-muted)" }}>· {m.lead}</span></span>
            <button type="button" data-testid={`encounter-section-${m.key}-preview-toggle`} onClick={() => setPreviewKeys((p) => ({ ...p, [m.key]: !p[m.key] }))} className="text-[11px] ink-link" style={{ color: "var(--burgundy)" }}>
              {previewKeys[m.key] ? "Edit" : "Preview"}
            </button>
          </div>
          {previewKeys[m.key] ? (
            <div className="rounded-lg border p-4 min-h-[60px]" style={card}>
              <Markdown style={{ color: "var(--text-obsidian)" }}>{d.sections[m.key]}</Markdown>
              {!(d.sections[m.key] || "").trim() && <span className="text-xs italic" style={{ color: "var(--text-muted)" }}>Empty — this section will not be shown.</span>}
            </div>
          ) : (
            <textarea data-testid={`encounter-section-${m.key}-input`} rows={m.key === "teaching" || m.key === "living_witness" || m.key === "engage" ? 10 : 6}
              value={d.sections[m.key] || ""} onChange={(e) => setSec(m.key, e.target.value)}
              className={inputCls + " font-mono text-[13px] leading-relaxed"} style={{ borderColor: "var(--border-cream)" }} />
          )}
          {m.key === "reflect" && (
            <Field label="Reflection prompt (saved with the member's private entry)" hint="Members' responses are private. Admins never see them.">
              <textarea data-testid="encounter-prompt-input" rows={2} value={d.prompt || ""} onChange={(e) => set("prompt", e.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
            </Field>
          )}
        </div>
      ))}

      <ResourcesEditor ownerType="encounter" ownerId={d.id} />

      <div className="flex flex-wrap items-center gap-3 mt-6 sticky bottom-0 py-3" style={{ background: "var(--bg-cream)" }}>
        <button data-testid="encounter-save-btn" onClick={() => save()} className="rounded-full px-5 py-2.5 text-sm font-medium" style={pill}>Save</button>
        {d.status !== "published" && <button data-testid="encounter-save-publish-btn" onClick={() => save("published")} className="rounded-full px-5 py-2.5 text-sm font-medium border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>Save &amp; publish</button>}
        <a href={`/learn/${course.id}?preview=1`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs ink-link" style={{ color: "var(--burgundy)" }}><Eye className="w-3.5 h-3.5" /> Preview course</a>
      </div>
    </div>
  );
}

function ResourcesEditor({ ownerType, ownerId }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const isCourse = ownerType === "course";
  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/learning/resources?owner_type=${ownerType}&owner_id=${ownerId}`);
    setRows(data || []);
  }, [ownerType, ownerId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!rows.some((r) => ["pending", "processing"].includes(r.ingest_status))) return;
    const t = setTimeout(load, 2500); return () => clearTimeout(t);
  }, [rows, load]);

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const path = isCourse ? `/admin/learning/courses/${ownerId}/resources` : `/admin/learning/encounters/${ownerId}/resources`;
      await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast("Resource attached"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("Remove this resource?")) return; await api.delete(`/admin/learning/resources/${id}`); load(); };
  const patch = async (id, body) => {
    try { await api.patch(`/admin/learning/resources/${id}`, body); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const rename = async (r) => { const t = window.prompt("Source title (used in citations)", r.title); if (t && t.trim() && t !== r.title) patch(r.id, { title: t.trim() }); };
  const reprocess = async (id) => { await api.post(`/admin/learning/resources/${id}/reprocess`); toast("Re-indexing…"); load(); };

  const ingestBadge = (r) => {
    const map = { ready: ["Indexed", "var(--teal)"], processing: ["Indexing…", "var(--gold-dark)"], pending: ["Queued", "var(--gold-dark)"], failed: ["Failed", "var(--rose-dark)"], not_indexed: ["Not indexed", "var(--text-muted)"] };
    const [label, color] = map[r.ingest_status] || map.not_indexed;
    return <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color }} title={r.ingest_error || ""}>{label}</span>;
  };

  return (
    <div className="rounded-xl border p-3 mb-3" style={card} data-testid={`resources-editor-${ownerType}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--gold-dark)" }}>{isCourse ? "Source library · downloads & Learning Companion" : "Attached resources"}</span>
        <label className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border cursor-pointer" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)", opacity: busy ? 0.6 : 1 }}>
          <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Attach file"}
          <input data-testid={`resource-upload-${ownerType}-input`} type="file" accept=".pdf,.docx,.txt,.md,.mp3,.wav,.m4a,.flac,.epub,.zip" className="hidden" disabled={busy} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
      </div>
      {isCourse && <p className="text-[11px] mb-2" style={{ color: "var(--text-muted)" }}>Two independent controls per source: <b>Approve for Companion</b> (indexed knowledge base — PDF, DOCX, TXT, MD) and <b>Show to members</b> (downloadable Course Resource). A source may be Companion-only, member-only, or both. Archived sources are hidden and removed from retrieval.</p>}
      {rows.length === 0 ? <div className="text-xs" style={{ color: "var(--text-muted)" }}>None yet. PDF, DOCX, TXT, audio, EPUB or ZIP — visible to signed-in members.</div> : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id} className="rounded-lg border p-2 text-sm" style={{ borderColor: "var(--border-cream)", background: "var(--bg-cream)" }} data-testid={`resource-row-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => rename(r)} className="font-medium text-left ink-link truncate" style={{ color: "var(--burgundy)" }} data-testid={`resource-rename-${r.id}-btn`}>{r.title}</button>
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>({r.original_name}, {Math.round((r.size_bytes || 0) / 1024)} KB)</span>
                <span className="flex-1" />
                {isCourse && <StatusBadge status={r.approval === "approved" ? "published" : r.approval || "draft"} />}
                {isCourse && ingestBadge(r)}
                <button onClick={() => remove(r.id)} className="p-1" data-testid={`resource-delete-${r.id}-btn`}><Trash2 className="w-3.5 h-3.5" style={{ color: "var(--rose-dark)" }} /></button>
              </div>
              {isCourse && (
                <div className="mt-1.5 flex items-center gap-3 text-[11px] flex-wrap">
                  {r.approval !== "approved" && <button data-testid={`resource-approve-${r.id}-btn`} onClick={() => patch(r.id, { approval: "approved" })} className="ink-link" style={{ color: "var(--teal)" }}>Approve for Companion</button>}
                  {r.approval === "approved" && <button data-testid={`resource-unapprove-${r.id}-btn`} onClick={() => patch(r.id, { approval: "draft" })} className="ink-link" style={{ color: "var(--burgundy)" }}>Remove from Companion</button>}
                  {r.approval !== "archived" && <button data-testid={`resource-archive-${r.id}-btn`} onClick={() => patch(r.id, { approval: "archived" })} className="ink-link" style={{ color: "var(--text-muted)" }}>Archive</button>}
                  <label className="inline-flex items-center gap-1 cursor-pointer" style={{ color: r.member_visible === false ? "var(--text-muted)" : "var(--teal-deep)" }}>
                    <input type="checkbox" data-testid={`resource-member-visible-${r.id}-toggle`} checked={r.member_visible !== false} onChange={(e) => patch(r.id, { member_visible: e.target.checked })} /> Show to members
                  </label>
                  {r.approval === "archived" && <button onClick={() => patch(r.id, { approval: "draft" })} className="ink-link" style={{ color: "var(--text-muted)" }}>Unarchive</button>}
                  {[".pdf", ".docx", ".txt", ".md"].includes(r.ext) && r.ingest_status !== "processing" && <button data-testid={`resource-reprocess-${r.id}-btn`} onClick={() => reprocess(r.id)} className="ink-link" style={{ color: "var(--text-muted)" }}>{r.ingest_status === "ready" ? "Re-index" : "Index now"}</button>}
                  {r.ingest_status === "ready" && <span style={{ color: "var(--text-muted)" }}>{r.chunk_count} passages{r.page_count ? ` · ${r.page_count} pages` : ""}</span>}
                  {r.ingest_status === "failed" && <span style={{ color: "var(--rose-dark)" }}>{r.ingest_error}</span>}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
